import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, CheckCircle2, Edit, Euro, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAlert } from './AlertProvider';

interface ProductTier {
    id?: number;
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
}

interface ProductConfig {
    productId: number;
    productTitle: string;
    marque: string;
    gamme: string;
    unit: string;
    customPrice: number;
    tierCount: number;
    tierMode: 'ug' | 'percent' | null;
    tiers: ProductTier[];
}

interface BpuProductTierModalProps {
    isOpen: boolean;
    onClose: () => void;
    bpuId: number;
    bpuName: string;
    products: Array<{
        id: number;
        consumable_id: number;
        custom_price: number;
        tiers?: ProductTier[];
        consumable?: {
            id: number;
            title: string;
            marque?: string;
            gamme?: string;
            unit?: string;
        };
    }>;
    onSuccess: () => void;
    initialProductId?: number;
}

const BpuProductTierModal: React.FC<BpuProductTierModalProps> = ({
    isOpen,
    onClose,
    bpuId,
    bpuName,
    products,
    onSuccess,
    initialProductId
}) => {
    const { showSuccess, showError } = useAlert();
    const [view, setView] = useState<'list' | 'edit'>('list');
    const [productConfigs, setProductConfigs] = useState<ProductConfig[]>([]);
    const [editingProduct, setEditingProduct] = useState<ProductConfig | null>(null);
    const [tiers, setTiers] = useState<ProductTier[]>([]);
    const [tierMode, setTierMode] = useState<'ug' | 'percent'>('percent');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadProductConfigs();
            // Reset view when modal opens
            if (!initialProductId) {
                setView('list');
                setEditingProduct(null);
            }
        } else {
            // Reset state when modal closes
            setView('list');
            setEditingProduct(null);
            setTiers([]);
        }
    }, [isOpen, initialProductId]);

    useEffect(() => {
        if (isOpen && initialProductId && productConfigs.length > 0) {
            const config = productConfigs.find(c => c.productId === initialProductId);
            if (config) {
                handleEditProduct(config);
            }
        }
    }, [isOpen, initialProductId, productConfigs]);

    const loadProductConfigs = () => {
        const configs: ProductConfig[] = products.map(item => {
            const consumable = item.consumable;
            const itemTiers = item.tiers || [];
            const firstTier = itemTiers[0];
            const mode = firstTier?.free_units !== null && firstTier?.free_units !== undefined ? 'ug' : 'percent';

            return {
                productId: item.id,
                productTitle: consumable?.title || 'Produit inconnu',
                marque: consumable?.marque || '',
                gamme: consumable?.gamme || '',
                unit: consumable?.unit || '',
                customPrice: item.custom_price,
                tierCount: itemTiers.length,
                tierMode: itemTiers.length > 0 ? mode : null,
                tiers: itemTiers
            };
        });

        setProductConfigs(configs);
    };

    const handleEditProduct = (config: ProductConfig) => {
        setEditingProduct(config);
        setTiers(config.tiers || []);
        setTierMode(config.tierMode || 'percent');
        setView('edit');
    };

    const handleBackToList = () => {
        setView('list');
        setEditingProduct(null);
        setTiers([]);
    };

    const addTier = () => {
        const newTier: ProductTier = {
            tier_number: tiers.length + 1,
            min_quantity: tiers.length > 0 ? tiers[tiers.length - 1].min_quantity + 10 : 10,
            max_quantity: null,
            discount_percentage: tierMode === 'percent' ? 5 : null,
            free_units: tierMode === 'ug' ? 1 : null
        };
        setTiers([...tiers, newTier]);
    };

    const removeTier = (index: number) => {
        const updatedTiers = tiers.filter((_, i) => i !== index);
        // Renumber tiers
        const renumberedTiers = updatedTiers.map((tier, i) => ({
            ...tier,
            tier_number: i + 1
        }));
        setTiers(renumberedTiers);
    };

    const updateTier = (index: number, field: keyof ProductTier, value: any) => {
        const updatedTiers = [...tiers];
        updatedTiers[index] = { ...updatedTiers[index], [field]: value };
        setTiers(updatedTiers);
    };

    const handleModeChange = (mode: 'ug' | 'percent') => {
        setTierMode(mode);
        setTiers([]);
    };

    const handleSave = async () => {
        if (!editingProduct || tiers.length === 0) {
            showError('Veuillez ajouter au moins un palier');
            return;
        }

        setLoading(true);
        try {
            // Prepare tiers data
            const tiersData = tiers.map(tier => ({
                tier_number: tier.tier_number,
                min_quantity: tier.min_quantity,
                max_quantity: tier.max_quantity,
                discount_percentage: tierMode === 'percent' ? tier.discount_percentage : null,
                free_units: tierMode === 'ug' ? tier.free_units : null
            }));

            // Send all tiers in a single request (replaces existing tiers)
            await api.post(`/client-pricing/${bpuId}/items/${editingProduct.productId}/tiers`, tiersData);

            showSuccess('Paliers enregistrés avec succès');
            onSuccess();
            onClose();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editingProduct) return;

        setLoading(true);
        try {
            // Delete all tiers for this product
            await api.delete(`/client-pricing/${bpuId}/items/${editingProduct.productId}/tiers`);

            showSuccess('Paliers supprimés avec succès');
            onSuccess();
            onClose();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <Euro className="w-6 h-6 text-emerald-600" />
                            Paliers de Tarification - {bpuName}
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            {view === 'list' 
                                ? `${productConfigs.length} produits`
                                : `${editingProduct?.productTitle}`
                            }
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {view === 'list' ? (
                        /* LIST VIEW - Show all products */
                        <div className="space-y-3">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : productConfigs.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Aucun produit disponible</p>
                                </div>
                            ) : (
                                productConfigs.map((config) => (
                                    <div
                                        key={config.productId}
                                        className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    {config.tierCount > 0 ? (
                                                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                                    ) : (
                                                        <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        <h3 className="font-semibold text-slate-900 dark:text-white">
                                                            {config.productTitle}
                                                        </h3>
                                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                                            {config.marque} • {config.gamme} • {config.unit} • {config.customPrice.toFixed(2)}€
                                                        </p>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                            {config.tierCount > 0 
                                                                ? `${config.tierCount} palier${config.tierCount > 1 ? 's' : ''} configuré${config.tierCount > 1 ? 's' : ''} (${config.tierMode === 'ug' ? 'UG' : '%'})`
                                                                : 'Aucun palier configuré'
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleEditProduct(config)}
                                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                                            >
                                                <Edit className="w-4 h-4" />
                                                {config.tierCount > 0 ? 'Modifier' : 'Configurer'}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        /* EDIT VIEW - Configure tiers for selected product */
                        <div className="space-y-6">
                            {/* Info box */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex gap-3">
                                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-800 dark:text-blue-200">
                                    <p className="font-medium mb-1">Paliers spécifiques au client</p>
                                    <p>Ces paliers s'appliquent uniquement à ce BPU et ne modifient pas la configuration globale.</p>
                                </div>
                            </div>

                            {/* Tier Mode Selection */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Type de palier
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleModeChange('ug')}
                                        className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                                            tierMode === 'ug'
                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                                : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                                        }`}
                                    >
                                        <div className="font-medium">Unités Gratuites (UG)</div>
                                    </button>
                                    <button
                                        onClick={() => handleModeChange('percent')}
                                        className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                                            tierMode === 'percent'
                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                                : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                                        }`}
                                    >
                                        <div className="font-medium">Remise en %</div>
                                    </button>
                                </div>
                            </div>

                            {/* Tiers Configuration */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Paliers de tarification
                                    </label>
                                    <button
                                        onClick={addTier}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Ajouter un palier
                                    </button>
                                </div>

                                {tiers.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                                        <p className="text-sm">Aucun palier configuré</p>
                                        <p className="text-xs mt-1">Cliquez sur "Ajouter un palier" pour commencer</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {tiers.map((tier, index) => (
                                            <div
                                                key={index}
                                                className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50"
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className="flex-shrink-0 w-20">
                                                        <label className="block text-xs text-slate-500 mb-1">Palier</label>
                                                        <div className="text-lg font-semibold text-orange-600">#{tier.tier_number}</div>
                                                    </div>

                                                    <div className="flex-1 grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-xs text-slate-500 mb-1">
                                                                Qté min
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={tier.min_quantity}
                                                                onChange={(e) => updateTier(index, 'min_quantity', parseInt(e.target.value) || 0)}
                                                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                                min="1"
                                                                step="1"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-xs text-slate-500 mb-1">
                                                                {tierMode === 'ug' ? 'Unités Gratuites' : 'Remise (%)'}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={tierMode === 'ug' ? (tier.free_units || 0) : (tier.discount_percentage || 0)}
                                                                onChange={(e) => {
                                                                    const field = tierMode === 'ug' ? 'free_units' : 'discount_percentage';
                                                                    updateTier(index, field, parseFloat(e.target.value) || 0);
                                                                }}
                                                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                                min="0"
                                                                step={tierMode === 'ug' ? 1 : 0.5}
                                                            />
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => removeTier(index)}
                                                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Supprimer ce palier"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-slate-200 dark:border-slate-700">
                    {view === 'list' ? (
                        /* List view footer */
                        <div className="flex items-center justify-between w-full">
                            <div className="text-sm text-slate-500">
                                {productConfigs.filter(c => c.tierCount > 0).length} / {productConfigs.length} configurés
                            </div>
                            <button
                                onClick={onClose}
                                className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    ) : (
                        /* Edit view footer */
                        <>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleBackToList}
                                    disabled={loading}
                                    className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Retour
                                </button>
                                {editingProduct?.tierCount && editingProduct.tierCount > 0 && (
                                    <button
                                        onClick={handleDelete}
                                        disabled={loading}
                                        className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 className="w-4 h-4 inline mr-2" />
                                        Supprimer tous les paliers
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleSave}
                                    disabled={loading || tiers.length === 0}
                                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Enregistrement...
                                        </>
                                    ) : (
                                        'Enregistrer'
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BpuProductTierModal;
